import React, { useCallback, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Table from 'antd/lib/table'

import apiManager from 'helpers/apiManager'
import useQuery from 'hooks/useQuery'
import { dateFormatter } from 'helpers'
import { ROUTES } from 'helpers/constants'
import { ChevronLeftIcon, PrimaryButton } from 'components'
import AddFeedbackModal from './AddFeedbackModal'

const profileColumns = [
  {
    title: 'Rank',
    dataIndex: 'rank',
    key: 'rank',
    width: 60,
  },
  {
    title: 'Name',
    dataIndex: 'fullName',
    key: 'fullName',
  },
  {
    title: 'Company',
    dataIndex: 'company',
    key: 'company',
  },
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',
  },
  {
    title: 'Match Score',
    dataIndex: 'matchScore',
    key: 'matchScore',
    width: 100,
    render: score => <span className="font-semibold">{score}%</span>,
  },
  {
    title: 'Match Reasoning',
    dataIndex: 'matchReasoning',
    key: 'matchReasoning',
    width: 400,
    render: text => <div className="text-sm text-gray-600">{text}</div>,
  },
]

const Section = ({ title, children, action }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between border-b pb-2 mb-4">
      <h2 className="text-2xl font-semibold">{title}</h2>
      {action}
    </div>
    {children}
  </div>
)

const InfoRow = ({ label, value }) => (
  <div className="flex mb-2">
    <span className="font-medium w-40 text-gray-600">{label}:</span>
    <span className="flex-1">{value || '-'}</span>
  </div>
)

const AdminAISearchSession = () => {
  const { sessionId } = useParams()
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)

  const { data: session, isLoading } = useQuery(`admin/search/sessions/${sessionId}`, () =>
    apiManager.adminGetSearchSession(sessionId)
  )

  const handleOpenFeedbackModal = useCallback(() => {
    setShowFeedbackModal(true)
  }, [])

  const handleCloseFeedbackModal = useCallback(() => {
    setShowFeedbackModal(false)
  }, [])

  const handleSaveFeedback = useCallback(
    async payload => {
      setIsSubmitting(true)
      try {
        await apiManager.adminCreateSearchSessionFeedback(sessionId, payload)
        window.location.reload()
      } catch (error) {
        console.error('Failed to save feedback:', error)
        setIsSubmitting(false)
      }
    },
    [sessionId]
  )

  const handleRegenerateRecommendations = useCallback(async () => {
    setIsRegenerating(true)
    try {
      await apiManager.adminRegenerateSearchSessionRecommendations(sessionId)
      window.location.reload()
    } catch (error) {
      console.error('Failed to regenerate recommendations:', error)
      setIsRegenerating(false)
    }
  }, [sessionId])

  if (isLoading) {
    return (
      <div className="w-full min-h-screen px-4 bg-white py-4 md:px-10">
        <div className="text-center py-10">Loading...</div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="w-full min-h-screen px-4 bg-white py-4 md:px-10">
        <div className="text-center py-10">Session not found</div>
      </div>
    )
  }

  return (
    <div className="w-full min-h-screen px-4 bg-white py-4 md:px-10">
      <div className="flex items-center justify-between mb-4">
        <Link
          to={ROUTES.ADMIN_AI_SEARCH}
          className="inline-flex items-center text-blue-500 hover:text-blue-700"
        >
          <ChevronLeftIcon className="w-5 h-5 mr-1" />
          Back to AI Search
        </Link>
        <PrimaryButton onClick={handleRegenerateRecommendations} disabled={isRegenerating}>
          {isRegenerating ? 'Regenerating...' : 'Regenerate Recommendations'}
        </PrimaryButton>
      </div>
      <h1 className="text-4xl text-center mb-8">Session Details</h1>

      <Section title="Basic Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InfoRow label="Session ID" value={session.id} />
            <InfoRow label="Client Name" value={session.client?.name} />
            <InfoRow label="Status" value={session.status} />
            <InfoRow label="Stage" value={session.stage} />
          </div>
          <div>
            <InfoRow label="Iteration" value={session.iteration} />
            <InfoRow label="Profiles Limit" value={session.profilesLimit} />
            <InfoRow
              label="Created At"
              value={session.createdAt ? dateFormatter.returnYYYYMMDD(session.createdAt) : '-'}
            />
            <InfoRow
              label="Updated At"
              value={session.updatedAt ? dateFormatter.returnYYYYMMDD(session.updatedAt) : '-'}
            />
          </div>
        </div>
        {session.categories && session.categories.length > 0 && (
          <div className="mt-4">
            <span className="font-medium text-gray-600">Categories: </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {session.categories.map((category, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>
        )}
      </Section>

      <Section title="Summary">
        <p className="text-gray-700 whitespace-pre-wrap">
          {session.summary || 'No summary available'}
        </p>
      </Section>

      <Section title="Requirements">
        <div className="bg-gray-50 p-4 rounded-lg">
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
            {session.requirements || 'No requirements specified'}
          </pre>
        </div>
      </Section>

      <Section title="Results">
        {session.results && session.results.length > 0 ? (
          session.results.map((result, index) => (
            <div key={result.id} className="mb-8 border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">
                  Result #{result.id}{' '}
                  {result.current && <span className="text-green-600">(Current)</span>}
                </h3>
                <span className="text-sm text-gray-500">
                  {result.createdAt ? dateFormatter.returnYYYYMMDD(result.createdAt) : ''}
                </span>
              </div>
              {result.notes && <p className="text-sm text-gray-600 mb-4 italic">{result.notes}</p>}
              <Table
                columns={profileColumns}
                dataSource={result.profiles?.map(p => ({ ...p, key: p.id })) || []}
                pagination={false}
                size="small"
              />
            </div>
          ))
        ) : (
          <p className="text-gray-500">No results available</p>
        )}
      </Section>

      <Section
        title="Feedback"
        action={<PrimaryButton onClick={handleOpenFeedbackModal}>Add Feedback</PrimaryButton>}
      >
        {session.feedback && session.feedback.length > 0 ? (
          <div className="space-y-4">
            {session.feedback.map(fb => (
              <div key={fb.id} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium">{fb.user?.name || 'Unknown User'}</span>
                    <span className="text-gray-500 text-sm ml-2">({fb.user?.email})</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {fb.createdAt ? dateFormatter.returnYYYYMMDD(fb.createdAt) : ''}
                  </span>
                </div>
                <p className="text-gray-700">{fb.notes}</p>
                {fb.resultId && (
                  <p className="text-sm text-gray-500 mt-2">For Result ID: {fb.resultId}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No feedback available</p>
        )}
      </Section>

      <AddFeedbackModal
        show={showFeedbackModal}
        onClose={handleCloseFeedbackModal}
        onSave={handleSaveFeedback}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default AdminAISearchSession
